import type { UnifiedProcessGraphData } from '../../../api/unified-process-graph-client/unified-process-graph-client.type';

/** How far a session's turns are expanded on the graph. Absent from the map = collapsed. */
enum TurnExpansion {
  Preview = 'preview',
  All = 'all',
}

/** Graph nodes a user can act on; drives the click and keyboard handling in the chart. */
enum InteractiveNodeKind {
  Session = 'session',
  Turn = 'turn',
  MoreTurns = 'more_turns',
  Comment = 'comment',
}

/** Where a lane sits and how it looks: its column `x`, its colour, and its label width. */
interface LaneStyle {
  x: number;
  bg: string;
  width: number;
}

/** A legend entry that toggles a group of lanes, with the x range its nodes occupy. */
interface LaneGroup {
  key: string;
  label: string;
  color: string;
  xRange: [number, number];
}

/** Hover preview of a PR comment node: its first comment, trimmed, plus how many more there are. */
interface CommentPreview {
  author: string | null;
  path: string | null;
  body: string;
  /** Comments on the node beyond the one previewed. */
  remaining: number;
}

/** A commit's card and hover content: short hash, first message line, full message and author. */
interface CommitPreview {
  hash: string;
  /** First non-empty line of the message. */
  subject: string;
  /** Full message, trimmed and capped in length. */
  message: string;
  author: string | null;
}

/** When a turn ran, in epoch ms. `endMs` is never before `startMs`. */
interface TurnTiming {
  turnId: string;
  turnNumber: number;
  startMs: number;
  endMs: number;
}

/** One item placed on the graph's timeline, before it becomes a ReactFlow node. */
interface TimelineEntry {
  timestamp: number;
  lane: string;
  id: string;
  label: string;
  parentId?: string;
  icon?: string;
  prIndex?: number;
  /**
   * Timestamp used to choose which task-event gap the entry is laid out in,
   * when it differs from `timestamp`. Turns use their session's start so they
   * stay grouped under the session even if they ran past the next task event.
   */
  bucketTimestamp?: number;
  /** Every turn's timing, set on expanded sessions only; drives the track. */
  turnTimings?: TurnTiming[];
  /** When a session ended (epoch ms); used to tell whether sessions overlap. */
  endTimestamp?: number;
  /** Where a turn or "+N more" card sits in time on its session's track. */
  trackTimeMs?: number;
  /** Raw agent session id (no prefix) for session, turn and "+N more" entries. */
  sessionId?: string;
  /** Raw turn id for turn entries. */
  turnId?: string;
  /** PR comment node id, for comment entries on the PR lifecycle lane. */
  commentId?: string;
  /** Short preview of a comment node's first comment, shown on hover. */
  commentPreview?: CommentPreview;
  /** Hash, subject and full message of a commit, shown on its card and on hover. */
  commitPreview?: CommitPreview;
}

/** A card's marker on its session's track, in absolute graph coordinates. */
interface TrackMarker {
  /** The turn or "+N more" card the marker belongs to. */
  cardId: string;
  sessionId: string;
  /** Set for turn markers; absent for the "+N more" marker. */
  turnId?: string;
  turnNumber?: number;
  durationMs?: number;
  /** Centre line of the step. */
  y: number;
}

/** The time between two consecutive steps, labelled midway between them. */
interface TrackGapLabel {
  y: number;
  label: string;
  isLong: boolean;
}

/** Layout of one expanded session's track. */
interface TurnBlock {
  sessionNodeId: string;
  trackTop: number;
  trackHeight: number;
  markers: TrackMarker[];
  gapLabels: TrackGapLabel[];
}

/** A session's running interval, for telling which session a commit belongs to. */
interface SessionInterval {
  id: string;
  startMs: number;
  endMs: number;
}

/** Props for the unified process graph canvas. */
interface UnifiedProcessGraphChartProps {
  data: UnifiedProcessGraphData;
  /** Called with the raw agent session id when a session node is clicked. */
  onSelectSession?: (sessionId: string) => void;
  /** Called with the raw session and turn ids when a turn node is clicked. */
  onSelectTurn?: (sessionId: string, turnId: string) => void;
  /** Agent session id whose node should render as selected. */
  selectedSessionId?: string | null;
  /** Turn id whose node should render as selected. */
  selectedTurnId?: string | null;
  /** Called with the PR comment node id when a comment node is clicked. */
  onSelectComment?: (commentId: string) => void;
  /** PR comment node id whose node should render as selected. */
  selectedCommentId?: string | null;
}

export { InteractiveNodeKind, TurnExpansion };
export type {
  CommentPreview,
  CommitPreview,
  LaneGroup,
  LaneStyle,
  SessionInterval,
  TimelineEntry,
  TrackGapLabel,
  TrackMarker,
  TurnBlock,
  TurnTiming,
  UnifiedProcessGraphChartProps,
};
